<script lang="ts">
  /* SupplierUpdates — what suppliers sent to our Products.

     1. «Новые позиции от поставщиков» (spec 20260915 §1.1): positions a
        supplier opened to us that are not in our Products yet — always the
        case when automatic adding is off in Settings, otherwise the ones we
        skipped. Added by hand, each supplier category filed «как у
        поставщика» or into a category of our own; the choice is remembered.
     2. The per-field review of supplier edits to linked cards. Since
        2026-09-15 every card field and the photos are compared, not ten.

     Lived as a banner on Partners → My suppliers; it is about products, so it
     is a Products tab now. */
  import { onMount } from "svelte";
  import { resizableColumns } from "$lib/columnWidths.svelte";
  import { _ } from "svelte-i18n";
  import { fetchCategories, type Category } from "$lib/catalog";
  import { formatBarcode, formatEur } from "$lib/format";
  import { productFieldLabel } from "$lib/productFieldLabels";
  import {
    dismissSupplierOffers,
    fetchPendingSupplierUpdates,
    fetchSupplierOffers,
    importSupplierOffers,
    resolveSupplierUpdate,
    type ProductUpdates,
    type SupplierOffer,
  } from "$lib/supplierDirectory";

  let {
    slug,
    /** Pending edits + new positions, so the host page's tab can show a badge. */
    oncount,
  }: {
    slug: string;
    oncount?: (n: number) => void;
  } = $props();

  let updates = $state<ProductUpdates[]>([]);
  let offers = $state<SupplierOffer[]>([]);
  let myCategories = $state<Category[]>([]);
  let loading = $state(true);
  let error = $state("");
  let notice = $state("");

  async function load() {
    loading = true;
    error = "";
    try {
      const [u, o] = await Promise.all([
        fetchPendingSupplierUpdates(slug).catch(() => [] as ProductUpdates[]),
        fetchSupplierOffers(slug),
      ]);
      updates = u;
      offers = o;
    } catch {
      error = "supdir.updates.error";
    } finally {
      loading = false;
    }
  }
  onMount(() => {
    load();
    fetchCategories(slug)
      .then((c) => (myCategories = c))
      .catch(() => (myCategories = []));
  });

  $effect(() => oncount?.(updates.length + offers.length));

  async function resolve(u: ProductUpdates, field: string, action: "accept" | "reject") {
    try {
      await resolveSupplierUpdate(slug, u.product_id, field, action);
      // Drop the resolved change locally; drop the product row when empty.
      u.changes = u.changes.filter((c) => c.field !== field);
      updates = updates.filter((x) => x.changes.length > 0);
    } catch {
      error = "supdir.updates.error";
    }
  }

  const fieldLabel = (field: string) => productFieldLabel(field, (k) => $_(k));

  // ── New positions ─────────────────────────────────────────────────────
  let selected = $state<Set<number>>(new Set());
  let busy = $state(false);
  const allChecked = $derived(offers.length > 0 && offers.every((o) => selected.has(o.product_id)));

  function toggle(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selected = next;
  }
  function toggleAll() {
    selected = allChecked ? new Set() : new Set(offers.map((o) => o.product_id));
  }

  function categoryPath(o: SupplierOffer): string {
    return [o.group_name, o.subgroup_name].filter(Boolean).join(" / ") || $_("offers.noCategory");
  }

  type Choice = {
    seller_category_id: number;
    label: string;
    mode: "mirror" | "mine";
    top: number | null;
    sub: number | null;
  };
  let choices = $state<Choice[]>([]);
  let importOpen = $state(false);

  const topCategories = $derived(myCategories.filter((c) => c.parent_id == null));
  const subsOf = (top: number | null) => myCategories.filter((c) => top != null && c.parent_id === top);
  const choicesValid = $derived(choices.every((c) => c.mode === "mirror" || c.top != null));

  function openImport() {
    const chosen = offers.filter((o) => selected.has(o.product_id));
    const byCategory = new Map<number, SupplierOffer>();
    for (const o of chosen) {
      if (o.seller_category_id != null && !byCategory.has(o.seller_category_id)) {
        byCategory.set(o.seller_category_id, o);
      }
    }
    choices = [...byCategory.values()].map((o) => {
      const mine = o.buyer_category_id != null ? myCategories.find((c) => c.id === o.buyer_category_id) : undefined;
      return {
        seller_category_id: o.seller_category_id!,
        label: categoryPath(o),
        mode: mine ? "mine" : "mirror",
        top: mine ? (mine.parent_id ?? mine.id) : null,
        sub: mine && mine.parent_id != null ? mine.id : null,
      };
    });
    if (choices.length === 0) confirmImport();
    else importOpen = true;
  }

  async function confirmImport() {
    busy = true;
    error = "";
    try {
      const ids = [...selected];
      const res = await importSupplierOffers(
        slug,
        ids,
        choices.map((c) => ({
          seller_category_id: c.seller_category_id,
          buyer_category_id: c.mode === "mine" ? (c.sub ?? c.top) : null,
        })),
      );
      notice = $_("offers.added", { values: { count: res.created } });
      importOpen = false;
      selected = new Set();
      await load();
      // «Как у поставщика» may have made categories of ours.
      myCategories = await fetchCategories(slug).catch(() => myCategories);
    } catch {
      error = "offers.error";
    } finally {
      busy = false;
    }
  }

  async function dismiss() {
    busy = true;
    error = "";
    try {
      await dismissSupplierOffers(slug, [...selected]);
      selected = new Set();
      await load();
    } catch {
      error = "offers.error";
    } finally {
      busy = false;
    }
  }
</script>

<div class="upd">
  {#if error}
    <div class="error-banner">{$_(error)}</div>
  {/if}
  {#if notice}
    <div class="notice-banner" role="status">{notice}</div>
  {/if}

  {#if loading}
    <div class="msg">{$_("common.loading")}</div>
  {:else if updates.length === 0 && offers.length === 0}
    <div class="msg">{$_("supdir.updates.empty")}</div>
  {:else}
    {#if offers.length > 0}
      <section class="offers-card">
        <h3 class="card-title">
          {$_("offers.title")}
          <span class="count-badge">{offers.length}</span>
        </h3>
        <p class="updates-hint">{$_("offers.hint")}</p>
        <div class="offers-bar">
          <label class="check-all">
            <input type="checkbox" checked={allChecked} onchange={toggleAll} />
            {$_("offers.selectAll")}
          </label>
          <span class="offers-selected">{$_("offers.selected", { values: { count: selected.size } })}</span>
          <button type="button" class="btn-action btn-action--primary" disabled={selected.size === 0 || busy} onclick={openImport}>
            {$_("offers.add")}
          </button>
          <button type="button" class="btn-action" disabled={selected.size === 0 || busy} onclick={dismiss}>
            {$_("offers.dismiss")}
          </button>
        </div>
        <div class="table-scroll">
          <table use:resizableColumns={"supplier-updates"} class="upd-table">
            <thead>
              <tr>
                <th></th>
                <th></th>
                <th>{$_("offers.col.product")}</th>
                <th>{$_("offers.col.supplier")}</th>
                <th>{$_("offers.col.category")}</th>
                <th class="num">{$_("offers.col.cost")}</th>
              </tr>
            </thead>
            <tbody>
              {#each offers as o (o.product_id)}
                <tr class:picked={selected.has(o.product_id)}>
                  <td class="td-check">
                    <input
                      type="checkbox"
                      checked={selected.has(o.product_id)}
                      onchange={() => toggle(o.product_id)}
                      aria-label={o.name}
                    />
                  </td>
                  <td class="td-img">
                    {#if o.image_url}<img src={o.thumb_url ?? o.image_url} alt="" loading="lazy" />{:else}<span class="img-ph">📦</span>{/if}
                  </td>
                  <td>
                    <div class="o-name">{o.name}</div>
                    <div class="o-code">{formatBarcode(o.ean) || o.sku || "-"}</div>
                  </td>
                  <td>{o.supplier_name}</td>
                  <td>{categoryPath(o)}</td>
                  <td class="num">{o.cost_price != null ? formatEur(o.cost_price) : "-"}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>
    {/if}

    {#if updates.length > 0}
      <section class="updates-card">
        <h3 class="card-title">{$_("supdir.updates.title")}</h3>
        <div class="updates-hint">{$_("supdir.updates.hint")}</div>
        {#each updates as u (u.product_id)}
          <div class="upd-product">
            <div class="upd-product-name">
              {u.product_name}
              {#if !u.canonical_is_active}
                <span class="pill pill--delisted">{$_("supdir.badge.delisted")}</span>
              {/if}
            </div>
            <div class="table-scroll">
              <table use:resizableColumns={"supplier-updates-fields"} class="upd-table">
                <thead>
                  <tr>
                    <th>{$_("supdir.updates.col.field")}</th>
                    <th>{$_("supdir.updates.col.mine")}</th>
                    <th>{$_("supdir.updates.col.supplier")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {#each u.changes as ch (ch.field)}
                    <tr>
                      <td class="upd-field">{fieldLabel(ch.field)}</td>
                      <td>{ch.mine ?? "-"}</td>
                      <td class="upd-new">{ch.supplier_value ?? "-"}</td>
                      <td class="td-actions">
                        <button
                          type="button"
                          class="btn-action btn-action--primary"
                          onclick={() => resolve(u, ch.field, "accept")}
                        >
                          {$_("supdir.updates.accept")}
                        </button>
                        <button
                          type="button"
                          class="btn-action"
                          onclick={() => resolve(u, ch.field, "reject")}
                        >
                          {$_("supdir.updates.reject")}
                        </button>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          </div>
        {/each}
      </section>
    {/if}
  {/if}
</div>

{#if importOpen}
  <div class="im-backdrop" role="presentation" onclick={(e) => e.target === e.currentTarget && !busy && (importOpen = false)}>
    <div class="im" role="dialog" aria-modal="true" aria-labelledby="im-title">
      <h3 id="im-title">{$_("offers.modal.title")}</h3>
      <p class="updates-hint">{$_("offers.modal.hint")}</p>
      {#each choices as c (c.seller_category_id)}
        <div class="im-row">
          <div class="im-cat">{c.label}</div>
          <label class="im-radio">
            <input type="radio" name={`m-${c.seller_category_id}`} checked={c.mode === "mirror"} onchange={() => (c.mode = "mirror")} />
            {$_("offers.modal.mirror")}
          </label>
          <label class="im-radio">
            <input type="radio" name={`m-${c.seller_category_id}`} checked={c.mode === "mine"} onchange={() => (c.mode = "mine")} />
            {$_("offers.modal.mine")}
          </label>
          {#if c.mode === "mine"}
            <div class="im-selects">
              <select
                aria-label={$_("pf.f.category")}
                value={c.top ?? ""}
                onchange={(e) => {
                  const v = e.currentTarget.value;
                  c.top = v ? Number(v) : null;
                  c.sub = null;
                }}
              >
                <option value="">{$_("pf.f.category")}…</option>
                {#each topCategories as cat (cat.id)}
                  <option value={cat.id}>{cat.name}</option>
                {/each}
              </select>
              <select
                aria-label={$_("pf.f.subcategory")}
                value={c.sub ?? ""}
                disabled={subsOf(c.top).length === 0}
                onchange={(e) => {
                  const v = e.currentTarget.value;
                  c.sub = v ? Number(v) : null;
                }}
              >
                <option value="">{$_("pf.f.subcategory")}…</option>
                {#each subsOf(c.top) as sub (sub.id)}
                  <option value={sub.id}>{sub.name}</option>
                {/each}
              </select>
            </div>
          {/if}
        </div>
      {/each}
      <div class="im-actions">
        <button type="button" class="btn btn--secondary" onclick={() => (importOpen = false)} disabled={busy}>
          {$_("common.cancel")}
        </button>
        <button type="button" class="btn btn--primary" onclick={confirmImport} disabled={busy || !choicesValid}>
          {busy ? $_("common.saving") : $_("offers.modal.confirm", { values: { count: selected.size } })}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .upd { display: flex; flex-direction: column; gap: 16px; }
  .error-banner { background: #fdecea; border: 1px solid #f5c6cb; padding: 12px 16px; border-radius: var(--radius); color: #c0392b; font-size: 14px; }
  .notice-banner { background: #eef7ee; border: 1px solid #c8e6c9; padding: 10px 16px; border-radius: var(--radius); color: #2e6b35; font-size: 14px; }
  .msg { padding: 40px; text-align: center; color: var(--color-text-muted); font-size: 14px; background: #fff; border: 1px solid var(--color-divider); border-radius: var(--radius); }

  .card-title { margin: 0 0 4px; font-size: 15px; display: flex; align-items: center; gap: 8px; color: var(--color-surface-darker); }
  .count-badge { background: var(--color-primary); color: #fff; border-radius: 10px; font-size: 11px; padding: 1px 8px; }
  .offers-card { background: #fff; border: 1px solid #cfe3f5; border-left: 4px solid var(--color-primary); border-radius: var(--radius); padding: 16px; }
  .offers-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 8px 0; font-size: 13px; }
  .check-all { display: flex; align-items: center; gap: 6px; cursor: pointer; }
  .offers-selected { color: var(--color-text-muted); margin-right: auto; }
  .td-check { width: 28px; }
  .td-img { width: 44px; }
  .td-img img { width: 36px; height: 36px; object-fit: contain; border-radius: 4px; border: 1px solid var(--color-divider); background: #fff; }
  .img-ph { display: inline-flex; width: 36px; height: 36px; align-items: center; justify-content: center; background: var(--color-bg); border-radius: 4px; }
  .o-name { font-weight: 600; }
  .o-code { color: var(--color-text-muted); font-size: 11px; }
  tr.picked td { background: #f3f8fd; }
  .table-scroll { overflow-x: auto; }

  .updates-card { background: #fff; border: 1px solid #ffe0b2; border-left: 4px solid #fb8c00; border-radius: var(--radius); padding: 16px; }
  .updates-hint { font-size: 12px; color: var(--color-text-muted); margin: 0 0 10px; }
  .upd-product { margin-top: 10px; }
  .upd-product-name { font-weight: 600; font-size: 13px; margin-bottom: 4px; }
  .upd-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .upd-table th { text-align: left; font-size: 10px; text-transform: uppercase; color: var(--color-text-muted); padding: 4px 8px; }
  .upd-table td { padding: 5px 8px; border-top: 1px solid var(--color-divider); vertical-align: middle; }
  .upd-table th.num, .upd-table td.num { text-align: right; white-space: nowrap; }
  .upd-field { color: var(--color-text-muted); }
  .upd-new { font-weight: 600; color: var(--color-surface-darker); }
  .td-actions { text-align: right; white-space: nowrap; }

  .pill { display: inline-block; padding: 2px 10px; border-radius: 10px; font-size: 11px; font-weight: 600; background: #f5f5f5; color: #757575; }
  .pill--delisted { background: #fdecea; color: #c0392b; margin-left: 6px; }

  .btn-action { display: inline-block; background: transparent; border: 1px solid var(--color-divider); padding: 4px 10px; border-radius: var(--radius); font-size: 12px; cursor: pointer; text-decoration: none; margin-left: 4px; color: var(--color-text); font-family: inherit; }
  .btn-action:disabled { opacity: 0.5; cursor: default; }
  .btn-action--primary { color: var(--color-primary); border-color: var(--color-primary); }
  .btn-action--primary:hover:not(:disabled) { background: var(--color-primary); color: #fff; }

  .im-backdrop { position: fixed; inset: 0; z-index: 1000; background: rgba(48, 56, 65, 0.45); display: flex; align-items: center; justify-content: center; padding: 16px; }
  .im { background: #fff; border-radius: 10px; width: 100%; max-width: 540px; max-height: calc(100vh - 32px); overflow-y: auto; padding: 18px 20px; box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2); }
  .im h3 { margin: 0 0 6px; font-size: 17px; }
  .im-row { border: 1px solid var(--color-divider); border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; display: flex; flex-direction: column; gap: 6px; font-size: 13.5px; }
  .im-cat { font-weight: 600; }
  .im-radio { display: flex; align-items: center; gap: 6px; cursor: pointer; }
  .im-selects { display: flex; gap: 8px; flex-wrap: wrap; padding-left: 22px; }
  .im-selects select { flex: 1 1 180px; min-width: 0; padding: 5px 8px; border: 1px solid var(--color-divider); border-radius: 6px; font-size: 13px; }
  .im-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 6px; flex-wrap: wrap; }
</style>
